import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import {
    ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
    Tooltip as RTooltip, ReferenceLine, BarChart, Bar, LabelList, Cell,
    RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
} from "recharts";

/* ═══════════════════════════════════════════════════════════════════════
 * HRDashboard — HR + admin. Six-Sigma / SPC analytics on the anonymous
 * engagement responses. Filters by department, tenure, terminal and period.
 * ═══════════════════════════════════════════════════════════════════════ */

const _bandThresholds = [4.2, 3.6, 3.0, 2.4]; // used by bandBg

function bandBg(mean) {
    if (mean == null) return "#F1F5F9";
    if (mean >= 4.2) return "#059669";
    if (mean >= 3.6) return "#22C55E";
    if (mean >= 3.0) return "#EAB308";
    if (mean >= 2.4) return "#F97316";
    return "#DC2626";
}

export default function HRDashboard() {
    const { user } = useAuth();
    const [config, setConfig] = useState(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState({
        department: "", tenure_band: "", terminal: "", period: "",
    });

    const load = async (f = filter) => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            for (const [k, v] of Object.entries(f)) if (v) params.set(k, v);
            const r = await api.get(`/engagement/dashboard${params.toString() ? `?${params}` : ""}`);
            setData(r.data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        api.get("/engagement/config").then((r) => setConfig(r.data));
        load({ department: "", tenure_band: "", terminal: "", period: "" });
    }, []);

    const applyFilter = (patch) => {
        const nf = { ...filter, ...patch };
        setFilter(nf); load(nf);
    };

    const downloadCsv = () => {
        const params = new URLSearchParams();
        for (const [k, v] of Object.entries(filter)) if (v) params.set(k, v);
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/engagement/export.csv${params.toString() ? `?${params}` : ""}`;
        window.open(url, "_blank");
    };

    const periods = data?.periods || [];
    const perSection = data?.per_section || [];
    const perQuestion = data?.per_question || [];

    const radarData = useMemo(() => perSection.map((s) => ({
        section: s.code, label: s.label, mean: s.mean || 0,
    })), [perSection]);

    return (
        <div className="min-h-screen bg-slate-50">
            <Header />
            <main className="max-w-7xl mx-auto px-4 md:px-8 py-6">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                    <div>
                        <div className="text-[11px] font-bold uppercase tracking-widest text-emerald-700">
                            HR People Analytics · Anonymous · Six-Sigma
                        </div>
                        <h1 className="text-2xl md:text-3xl font-black text-slate-900">
                            Employee Engagement Dashboard
                        </h1>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Signed in as <b>{user?.name}</b> · Role: {user?.role}
                        </p>
                    </div>
                    <button onClick={downloadCsv}
                            data-testid="hr-export-csv"
                            className="px-4 py-2 rounded bg-emerald-600 text-white text-xs font-black uppercase tracking-wider hover:bg-emerald-700">
                        Export CSV
                    </button>
                </div>

                {/* Filter bar */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-6 bg-white rounded-xl border p-3">
                    <FilterSelect label="Department" value={filter.department}
                                  testid="hr-filter-dept"
                                  options={config?.departments || []}
                                  onChange={(v) => applyFilter({ department: v })} />
                    <FilterSelect label="Time with company" value={filter.tenure_band}
                                  testid="hr-filter-tenure"
                                  options={config?.tenure_bands || []}
                                  onChange={(v) => applyFilter({ tenure_band: v })} />
                    <FilterSelect label="Terminal" value={filter.terminal}
                                  testid="hr-filter-terminal"
                                  options={(config?.terminals || []).map((t) => ({ value: t.key, label: t.label }))}
                                  onChange={(v) => applyFilter({ terminal: v })} />
                    <FilterSelect label="Period" value={filter.period}
                                  testid="hr-filter-period"
                                  options={periods.map((p) => ({ value: p.period, label: p.period_human }))}
                                  onChange={(v) => applyFilter({ period: v })} />
                    <button onClick={() => applyFilter({ department: "", tenure_band: "", terminal: "", period: "" })}
                            data-testid="hr-filter-reset"
                            className="self-end px-3 py-2 rounded bg-slate-200 hover:bg-slate-300 text-xs font-bold uppercase tracking-wider">
                        Reset
                    </button>
                </div>

                {loading && <div className="text-slate-500 text-sm">Loading…</div>}

                {data && (
                    <>
                        {/* Top KPI row */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                            <KpiCard title="Responses"
                                     value={data.total_responses}
                                     sub={`current period ${data.current_period_human}`}
                                     testid="hr-kpi-responses" />
                            <KpiCard title="Overall mean"
                                     value={data.overall?.mean ?? "—"}
                                     sub={data.overall?.mean != null ? `σ ${data.overall.sigma} · median ${data.overall.median}` : "no data"}
                                     tint={bandBg(data.overall?.mean)}
                                     testid="hr-kpi-mean" />
                            <KpiCard title="% Favourable"
                                     value={data.overall?.pct_favourable != null ? `${data.overall.pct_favourable}%` : "—"}
                                     sub={`% agree / strongly agree`}
                                     testid="hr-kpi-fav" />
                            <KpiCard title="% Unfavourable"
                                     value={data.overall?.pct_unfavourable != null ? `${data.overall.pct_unfavourable}%` : "—"}
                                     sub={`% disagree / strongly disagree`}
                                     testid="hr-kpi-unfav" />
                        </div>

                        {/* Radar + Trend row */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                            <Panel title="Category profile (radar)" testid="hr-radar">
                                <ResponsiveContainer width="100%" height={280}>
                                    <RadarChart data={radarData}>
                                        <PolarGrid />
                                        <PolarAngleAxis dataKey="section" tick={{ fontSize: 11, fontWeight: 600 }} />
                                        <PolarRadiusAxis angle={90} domain={[0, 5]} tick={{ fontSize: 10 }} />
                                        <Radar name="Mean" dataKey="mean"
                                               stroke="#065F46" fill="#10B981" fillOpacity={0.3} />
                                        <RTooltip />
                                    </RadarChart>
                                </ResponsiveContainer>
                                <div className="text-[10px] text-slate-500 mt-1">
                                    A · Clarity  B · Motivation  C · Management  D · Teamwork  E · Development  F · Retention
                                </div>
                            </Panel>

                            <Panel title="Trend over periods (control chart, ±3σ)" testid="hr-trend">
                                {periods.length === 0
                                    ? <div className="text-xs text-slate-500 py-8 text-center">No period data yet.</div>
                                    : <TrendChart periods={periods} />}
                            </Panel>
                        </div>

                        {/* Section leaderboard */}
                        <Panel title="Section leaderboard (mean · % favourable)" testid="hr-section-bar">
                            <ResponsiveContainer width="100%" height={260}>
                                <BarChart data={perSection} margin={{ left: 8, right: 8 }}>
                                    <CartesianGrid strokeDasharray="3 3" opacity={0.35} />
                                    <XAxis dataKey="code" tick={{ fontSize: 11, fontWeight: 700 }} />
                                    <YAxis domain={[0, 5]} tick={{ fontSize: 10 }} />
                                    <RTooltip content={<SectionTooltip />} />
                                    <ReferenceLine y={4} stroke="#DC2626" strokeDasharray="4 4" />
                                    <Bar dataKey="mean" radius={[6,6,0,0]}>
                                        {perSection.map((s, i) => (
                                            <Cell key={i} fill={bandBg(s.mean)} />
                                        ))}
                                        <LabelList dataKey="mean" position="top"
                                                    fontSize={11} fontWeight={700} fill="#0F172A" />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-2 mt-2">
                                {perSection.map((s) => (
                                    <div key={s.code} className="rounded-lg border p-2">
                                        <div className="text-[10px] text-slate-500 uppercase font-bold">{s.code}</div>
                                        <div className="text-[11px] text-slate-800 font-semibold leading-tight">{s.label}</div>
                                        <div className="mt-1 text-lg font-black" style={{ color: bandBg(s.mean) }}>
                                            {s.mean ?? "—"}
                                        </div>
                                        <div className="text-[10px] text-slate-500">
                                            {s.pct_favourable != null ? `${s.pct_favourable}% favourable` : ""}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Panel>

                        {/* Per-question distribution */}
                        <Panel title="Per-question distribution · mean · σ · ±3σ" testid="hr-questions"
                               className="mt-4">
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="text-left text-slate-500 border-b">
                                            <th className="py-1.5 pr-2">#</th>
                                            <th className="py-1.5 pr-2">Question</th>
                                            <th className="py-1.5 pr-2 text-right">n</th>
                                            <th className="py-1.5 pr-2 text-right">Mean</th>
                                            <th className="py-1.5 pr-2 text-right">σ</th>
                                            <th className="py-1.5 pr-2 text-right">LCL</th>
                                            <th className="py-1.5 pr-2 text-right">UCL</th>
                                            <th className="py-1.5 pr-2 text-right">Fav %</th>
                                            <th className="py-1.5 pr-2 text-right">Unfav %</th>
                                            <th className="py-1.5 pr-2">Distribution</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {perQuestion.map((q, idx) => (
                                            <tr key={q.key} className="border-b last:border-0 hover:bg-slate-50"
                                                data-testid={`hr-q-row-${q.key}`}>
                                                <td className="py-1.5 pr-2 text-slate-400 font-mono">{idx + 1}</td>
                                                <td className="py-1.5 pr-2 min-w-[280px]">
                                                    <div className="text-slate-800">{q.text}</div>
                                                    <div className="text-[10px] text-slate-500">{q.section} · {q.section_label}</div>
                                                </td>
                                                <td className="py-1.5 pr-2 text-right">{q.n}</td>
                                                <td className="py-1.5 pr-2 text-right font-bold"
                                                    style={{ color: bandBg(q.mean) }}>
                                                    {q.mean ?? "—"}
                                                </td>
                                                <td className="py-1.5 pr-2 text-right">{q.sigma ?? "—"}</td>
                                                <td className="py-1.5 pr-2 text-right text-slate-500">{q.lcl ?? "—"}</td>
                                                <td className="py-1.5 pr-2 text-right text-slate-500">{q.ucl ?? "—"}</td>
                                                <td className="py-1.5 pr-2 text-right text-emerald-700 font-bold">{q.pct_favourable ?? "—"}%</td>
                                                <td className="py-1.5 pr-2 text-right text-red-600 font-bold">{q.pct_unfavourable ?? "—"}%</td>
                                                <td className="py-1.5 pr-2 min-w-[140px]">
                                                    <DistBar dist={q.distribution} />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Panel>

                        {/* Heatmaps */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
                            <Heatmap title="Department × Category (mean 1-5)"
                                     rows={data.department_heatmap || []}
                                     rowKey="department"
                                     sections={data.sections || []}
                                     testid="hr-dept-heatmap" />
                            <Heatmap title="Tenure × Category (mean 1-5)"
                                     rows={data.tenure_heatmap || []}
                                     rowKey="tenure_band"
                                     sections={data.sections || []}
                                     testid="hr-tenure-heatmap" />
                        </div>

                        {/* Comments feed */}
                        <Panel title={`Anonymous comments (${data.comments?.length || 0})`}
                               testid="hr-comments" className="mt-4">
                            {(data.comments || []).length === 0
                                ? <div className="text-xs text-slate-500">No comments yet.</div>
                                : <div className="space-y-2 max-h-96 overflow-y-auto">
                                      {data.comments.map((c) => (
                                          <div key={c.id} className="rounded-lg border p-2.5 text-sm bg-slate-50/50">
                                              <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-2 flex-wrap">
                                                  <span>{c.period_human}</span>
                                                  {c.department && <span className="bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded text-[9px]">{c.department}</span>}
                                                  {c.tenure_band && <span className="bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded text-[9px]">{c.tenure_band}</span>}
                                                  {c.avg != null && (
                                                      <span className="ml-auto text-[10px]" style={{ color: bandBg(c.avg) }}>
                                                          personal mean {c.avg}
                                                      </span>
                                                  )}
                                              </div>
                                              <div className="text-slate-800 whitespace-pre-wrap">{c.comment}</div>
                                          </div>
                                      ))}
                                  </div>}
                        </Panel>

                        <div className="text-[10px] text-slate-400 mt-4 text-center pb-8">
                            All results are aggregated and anonymised. Individual responses are never surfaced.
                        </div>
                    </>
                )}
            </main>
        </div>
    );
}

function FilterSelect({ label, value, options, onChange, testid }) {
    const isObjArr = options.length > 0 && typeof options[0] === "object";
    return (
        <label className="block text-xs">
            <span className="font-bold text-slate-700 uppercase tracking-wider text-[10px]">{label}</span>
            <select value={value} onChange={(e) => onChange(e.target.value)}
                    data-testid={testid}
                    className="ttv-input mt-1">
                <option value="">All</option>
                {options.map((o) => isObjArr
                    ? <option key={o.value} value={o.value}>{o.label}</option>
                    : <option key={o} value={o}>{o}</option>)}
            </select>
        </label>
    );
}

function KpiCard({ title, value, sub, tint, testid }) {
    return (
        <div className="rounded-xl bg-white border p-4 relative overflow-hidden" data-testid={testid}>
            {tint && <div className="absolute inset-x-0 top-0 h-1" style={{ backgroundColor: tint }} />}
            <div className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">{title}</div>
            <div className="text-3xl font-black mt-1 text-slate-900">{value}</div>
            <div className="text-[10px] text-slate-500 mt-1">{sub}</div>
        </div>
    );
}

function Panel({ title, children, testid, className = "" }) {
    return (
        <div className={`rounded-xl bg-white border p-4 ${className}`} data-testid={testid}>
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">{title}</div>
            {children}
        </div>
    );
}

function SectionTooltip({ active, payload }) {
    if (!active || !payload?.length) return null;
    const s = payload[0].payload;
    return (
        <div className="bg-white rounded shadow border p-2 text-xs">
            <div className="font-bold">{s.code} · {s.label}</div>
            <div>Mean: <b>{s.mean ?? "—"}</b></div>
            <div>Favourable: <b>{s.pct_favourable ?? "—"}%</b></div>
        </div>
    );
}

function DistBar({ dist }) {
    const total = Object.values(dist || {}).reduce((a, b) => a + b, 0) || 1;
    return (
        <div className="flex h-3.5 w-full rounded overflow-hidden bg-slate-100">
            {[1,2,3,4,5].map((v) => {
                const c = dist?.[String(v)] || 0;
                const pct = (c / total) * 100;
                if (pct === 0) return null;
                const colors = ["#DC2626","#F97316","#94A3B8","#22C55E","#065F46"];
                return (
                    <div key={v} title={`${c} chose ${v}`}
                         style={{ width: `${pct}%`, backgroundColor: colors[v - 1] }} />
                );
            })}
        </div>
    );
}

function TrendChart({ periods }) {
    // Include ±3σ bands per period using each period's own sigma.
    const data = periods.map((p) => ({
        period: p.period_human, mean: p.mean, sigma: p.sigma,
        upper: p.mean != null && p.sigma != null ? Math.min(5, p.mean + 3 * p.sigma) : null,
        lower: p.mean != null && p.sigma != null ? Math.max(1, p.mean - 3 * p.sigma) : null,
        responses: p.responses,
    }));
    return (
        <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data} margin={{ left: 8, right: 8, top: 8 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.35} />
                <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                <YAxis domain={[1, 5]} tick={{ fontSize: 10 }} />
                <RTooltip />
                <ReferenceLine y={4} stroke="#DC2626" strokeDasharray="4 4" label={{ value: "Target 4", fontSize: 10, fill: "#DC2626" }} />
                <Line type="monotone" dataKey="upper" stroke="#94A3B8" strokeDasharray="3 3" dot={false} name="UCL (+3σ)" />
                <Line type="monotone" dataKey="lower" stroke="#94A3B8" strokeDasharray="3 3" dot={false} name="LCL (-3σ)" />
                <Line type="monotone" dataKey="mean" stroke="#065F46" strokeWidth={3}
                      dot={{ r: 4, fill: "#065F46" }} name="Mean" />
            </LineChart>
        </ResponsiveContainer>
    );
}

function Heatmap({ title, rows, rowKey, sections, testid }) {
    return (
        <Panel title={title} testid={testid}>
            {rows.length === 0
                ? <div className="text-xs text-slate-500 py-6 text-center">Not enough data yet.</div>
                : <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                          <thead>
                              <tr className="text-left text-slate-500">
                                  <th className="py-1 pr-2">{rowKey === "department" ? "Department" : "Tenure"}</th>
                                  {sections.map((s) => (
                                      <th key={s.code} className="py-1 px-1 text-center font-bold">{s.code}</th>
                                  ))}
                                  <th className="py-1 pl-2 text-right text-[10px]">n</th>
                              </tr>
                          </thead>
                          <tbody>
                              {rows.map((r) => (
                                  <tr key={r[rowKey]} className="border-t last:border-b">
                                      <td className="py-1 pr-2 font-semibold text-slate-700">{r[rowKey]}</td>
                                      {sections.map((s) => {
                                          const v = r[s.code];
                                          return (
                                              <td key={s.code} className="py-1 px-0.5">
                                                  <div className="rounded text-center py-1 text-white text-[11px] font-bold"
                                                       style={{ backgroundColor: v != null ? bandBg(v) : "#E5E7EB",
                                                                color: v == null ? "#9CA3AF" : "white" }}>
                                                      {v ?? "—"}
                                                  </div>
                                              </td>
                                          );
                                      })}
                                      <td className="py-1 pl-2 text-right text-[10px] text-slate-500">{r.responses}</td>
                                  </tr>
                              ))}
                          </tbody>
                      </table>
                  </div>}
        </Panel>
    );
}
